import { useTranslation } from 'react-i18next';
import type { ChartFigure, ChartPoint } from '@/lib/patientReports/chartFigure';
import { chartFillRatio, chartPng } from '@/lib/patientReports/chartFigure';
import type { HeadingStyle, ReportPreviewModel } from '@/lib/patientReports/template';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';

function visibleBlocks(model: ReportPreviewModel) {
  return model.blocks.filter(
    (block) =>
      block.heading.trim().length > 0 ||
      block.body.trim().length > 0 ||
      block.charts.length > 0 ||
      (block.tocEntries?.length ?? 0) > 0,
  );
}

function formatChartNumber(value: number): string {
  if (!Number.isFinite(value)) return '0';
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : String(rounded);
}

function ChartView({ chart }: { chart: ChartFigure }) {
  if (chart.kind === 'radar') return <RadarView chart={chart} />;
  if (chart.kind === 'scale') return <ScaleView chart={chart} />;
  if (chart.kind === 'history') return <HistoryView chart={chart} />;
  return (
    <div>
      <div className="flex justify-between text-xs text-neutral-600">
        <span>{chart.label}</span>
        <span>
          {formatChartNumber(chart.value)}
          {chart.max ? ` / ${formatChartNumber(chart.max)}` : ''}
        </span>
      </div>
      <div className="mt-s1 h-2 bg-neutral-200">
        <div
          className="h-2 bg-teal-700"
          style={{ width: `${chartFillRatio(chart) * 100}%` }}
        />
      </div>
    </div>
  );
}

function pngDataUrl(bytes: Uint8Array): string {
  let binary = '';
  const step = 0x8000;
  for (let index = 0; index < bytes.length; index += step) {
    binary += String.fromCharCode(...bytes.subarray(index, index + step));
  }
  return `data:image/png;base64,${btoa(binary)}`;
}

function RadarView({ chart }: { chart: ChartFigure }) {
  const names = (chart.points ?? []).map((point) => `${point.label} ${point.value}`).join(', ');
  return (
    <figure>
      <figcaption className="text-xs font-semibold text-neutral-700">{chart.label}</figcaption>
      <img
        alt={names ? `${chart.label}. ${names}` : chart.label}
        src={pngDataUrl(chartPng(chart))}
        className="mt-s1 w-full max-w-[32rem]"
      />
    </figure>
  );
}

const SCALE_CLASS = ['bg-emerald-300', 'bg-lime-300', 'bg-amber-300', 'bg-orange-300', 'bg-red-300'];

function ScaleView({ chart }: { chart: ChartFigure }) {
  const bands = chart.points ?? [];
  const end = chart.max > 0 ? chart.max : 1;
  return (
    <figure>
      <figcaption className="flex justify-between text-xs text-neutral-600">
        <span>{chart.label}</span>
        <span>
          {formatChartNumber(chart.value)}
          {chart.max ? ` / ${formatChartNumber(chart.max)}` : ''}
        </span>
      </figcaption>
      <div className="relative mt-s2 h-3">
        <div
          className="grid h-3 overflow-hidden bg-neutral-200"
          style={{
            gridTemplateColumns:
              bands.length > 0
                ? bands
                    .map((band) => `${Math.max(1, (band.max ?? band.value) - band.value + 1)}fr`)
                    .join(' ')
                : '1fr',
          }}
        >
          {(bands.length > 0 ? bands : [{ label: '', value: 0, max: end }]).map((band, index) => (
            <span key={`${band.label}-${index}`} className={cn('block h-3', SCALE_CLASS[index % SCALE_CLASS.length])} />
          ))}
        </div>
        <span
          className="absolute top-0 h-3 w-0.5 bg-neutral-900"
          style={{ left: `${chartFillRatio(chart) * 100}%` }}
          aria-hidden
        />
      </div>
      {bands.length > 0 ? (
        <p className="mt-s1 text-[11px] leading-4 text-neutral-500">
          {bands.map((band) => bandLine(band)).join(' · ')}
        </p>
      ) : null}
    </figure>
  );
}

function bandLine(band: ChartPoint): string {
  const end = band.max ?? band.value;
  return `${band.label} ${formatChartNumber(band.value)}–${formatChartNumber(end)}`;
}

function HistoryView({ chart }: { chart: ChartFigure }) {
  const points = chart.points ?? [];
  const width = 320;
  const height = 120;
  const left = 12;
  const right = width - 12;
  const top = 16;
  const bottom = height - 28;
  const max = Math.max(chart.max, ...points.map((point) => point.value), 1);
  const yOf = (value: number) => bottom - (Math.max(0, value) / max) * (bottom - top);
  const xOf = (index: number) =>
    points.length <= 1 ? (left + right) / 2 : left + (index * (right - left)) / (points.length - 1);
  const line = points.map((point, index) => `${xOf(index)},${yOf(point.value)}`).join(' ');
  return (
    <figure>
      <figcaption className="text-xs font-semibold text-neutral-700">{chart.label}</figcaption>
      <svg viewBox={`0 0 ${width} ${height}`} className="mt-s1 h-32 w-full" role="img" aria-label={chart.label}>
        <line x1={left} y1={bottom} x2={right} y2={bottom} stroke="#d6d3cc" />
        <polyline points={line} fill="none" stroke="#0f766e" strokeWidth="2" />
        {points.map((point, index) => (
          <g key={`${point.label}-${index}`}>
            <circle cx={xOf(index)} cy={yOf(point.value)} r={index === points.length - 1 ? 4 : 3} fill="#0f766e" />
            <text x={xOf(index)} y={height - 8} textAnchor="middle" fontSize="9" fill="#57534e">
              {point.label}
            </text>
          </g>
        ))}
      </svg>
    </figure>
  );
}

function headingClass(style: HeadingStyle | undefined): string {
  if (style === 'italic') return 'italic';
  if (style === 'both') return 'font-bold italic';
  return 'font-bold';
}

export function ReportPreview({
  model,
  letterheadLabel,
}: {
  model: ReportPreviewModel;
  letterheadLabel: string;
}) {
  const { t } = useTranslation();
  const blocks = visibleBlocks(model);
  return (
    <section aria-label={t('patientReports.preview')}>
      <h3 className={cn(TYPE.section, 'text-ds-text')}>{t('patientReports.preview')}</h3>
      <p className={cn(TYPE.small, 'mt-s1 text-ds-muted')}>{t('patientReports.preview_hint')}</p>
      <article
        data-testid="report-preview"
        className="mx-auto mt-s4 w-full max-w-[42rem] border border-ds-line bg-white px-[calc(var(--s6)/3)] py-s6 text-[#1c1917] shadow-pop"
      >
        <p className="border-b border-neutral-200 pb-s3 text-xs uppercase tracking-wide text-neutral-500">
          {letterheadLabel}
        </p>
        {model.cover ? (
          <div className="mt-s5 border-b border-dashed border-neutral-300 pb-s6" data-preview-cover>
            <p className="text-xs uppercase tracking-wide text-neutral-500">
              {t('patientReports.cover')}
            </p>
            <h2 className="mt-s3 font-serif text-2xl">{model.cover.title || model.title}</h2>
            {model.cover.body ? (
              <p className="mt-s3 whitespace-pre-wrap text-sm leading-6">{model.cover.body}</p>
            ) : null}
          </div>
        ) : null}
        <h2 className="mt-s5 font-serif text-2xl" data-preview-title>
          {model.title || t('patientReports.untitled')}
        </h2>
        {blocks.length === 0 ? (
          <p className="mt-s4 text-sm text-neutral-500">{t('patientReports.preview_empty')}</p>
        ) : (
          <div className="mt-s5 flex flex-col gap-s5">
            {blocks.map((block) => (
              <section key={block.id}>
                {block.heading ? (
                  <h3
                    className={cn('font-serif text-lg', headingClass(block.headingStyle))}
                    data-preview-heading
                    data-heading-style={block.headingStyle ?? 'bold'}
                  >
                    {block.heading}
                  </h3>
                ) : null}
                {block.tocEntries && block.tocEntries.length > 0 ? (
                  <div className="mt-s2 flex flex-col gap-s1">
                    {block.tocEntries.map((entry, index) => (
                      <p
                        key={`${entry.label}-${index}`}
                        className="text-sm font-normal not-italic leading-6"
                        data-preview-toc-entry
                        data-heading-style="plain"
                      >
                        {entry.label}
                      </p>
                    ))}
                  </div>
                ) : block.body ? (
                  <p className="mt-s2 whitespace-pre-wrap text-sm leading-6">{block.body}</p>
                ) : null}
                {block.charts.length > 0 ? (
                  <div className="mt-s3 flex flex-col gap-s3">
                    {block.charts.map((chart) => (
                      <ChartView key={chart.id ?? chart.label} chart={chart} />
                    ))}
                  </div>
                ) : null}
              </section>
            ))}
          </div>
        )}
      </article>
    </section>
  );
}
